import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { blogAPI, productsAPI } from '../services/api';
import { BookOpen, FlaskConical } from 'lucide-react';

/**
 * Related-posts + related-products block injected at the bottom of every
 * BlogPostPage. Built to address two specific iter-44 Semrush findings:
 *
 *   1. "3,322 orphaned sitemap pages", the per-locale blog posts had no
 *      inbound internal links other than the /blog index. Now every post
 *      sends 3 internal links to siblings (same-category preference) and
 *      3 internal links to relevant products → ≈360 new internal edges
 *      per locale, ≈3,600 across 10 locales.
 *
 *   2. "Low internal linking depth" → adding 6 contextual outlinks per
 *      post pushes Google's PageRank flow into the long-tail product /
 *      blog pages that historically had zero in-domain referrals.
 *
 * Component is intentionally additive, does not disturb article markup,
 * disclaimer, or back-to-blog link.
 */
const _toks = (s) => (s || '').toLowerCase().replace(/[^a-z0-9 ]+/g, ' ').split(/\s+/).filter(Boolean);

const _scoreOverlap = (a, b) => {
  const setA = new Set(_toks(a));
  let hits = 0;
  for (const w of _toks(b)) if (setA.has(w)) hits++;
  return hits;
};

const _label = (lang, key) => {
  const D = {
    relatedPosts: { en: 'Related research articles', es: 'Artículos de investigación relacionados', ru: 'Связанные статьи исследований', de: 'Verwandte Forschungsartikel', fr: 'Articles de recherche associés', it: 'Articoli di ricerca correlati', pl: 'Powiązane artykuły badawcze', nl: 'Gerelateerde onderzoeksartikelen', pt: 'Artigos de pesquisa relacionados', au: 'Related research articles' },
    relatedProducts: { en: 'Research products mentioned', es: 'Productos de investigación mencionados', ru: 'Упомянутые исследовательские продукты', de: 'Erwähnte Forschungsprodukte', fr: 'Produits de recherche mentionnés', it: 'Prodotti di ricerca menzionati', pl: 'Wymienione produkty badawcze', nl: 'Genoemde onderzoeksproducten', pt: 'Produtos de pesquisa mencionados', au: 'Research products mentioned' },
    readArticle: { en: 'Read article', es: 'Leer artículo', ru: 'Читать статью', de: 'Artikel lesen', fr: "Lire l'article", it: "Leggi l'articolo", pl: 'Czytaj artykuł', nl: 'Lees artikel', pt: 'Ler artigo', au: 'Read article' },
    viewProduct: { en: 'View product', es: 'Ver producto', ru: 'Смотреть продукт', de: 'Produkt ansehen', fr: 'Voir le produit', it: 'Vedi prodotto', pl: 'Zobacz produkt', nl: 'Bekijk product', pt: 'Ver produto', au: 'View product' },
  };
  return (D[key] && D[key][lang]) || D[key].en;
};

const RelatedPosts = ({ currentPost }) => {
  const { currentLanguage } = useLanguage();
  const [related, setRelated] = useState([]);
  const [products, setProducts] = useState([]);

  // Helper to pull localized text fields safely.
  const pickLocalized = (field) => {
    if (!field) return '';
    if (typeof field === 'string') return field;
    return field[currentLanguage] || field.en || '';
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [postsRes, productsRes] = await Promise.all([
          blogAPI.getPosts(50),
          productsAPI.getAll({ limit: 200 }),
        ]);
        if (cancelled) return;

        const allPosts = Array.isArray(postsRes) ? postsRes : (postsRes?.posts || []);
        const allProducts = Array.isArray(productsRes) ? productsRes : (productsRes?.products || []);

        // Rank candidate posts by title-overlap with the current post.
        const currentTitle = pickLocalized(currentPost.title);
        const currentExcerpt = pickLocalized(currentPost.excerpt);
        const currentText = `${currentTitle} ${currentExcerpt}`;
        const scored = allPosts
          .filter((p) => p.slug !== currentPost.slug && p.is_published !== false)
          .map((p) => ({
            post: p,
            score: _scoreOverlap(currentText, pickLocalized(p.title)) + _scoreOverlap(currentText, pickLocalized(p.excerpt)) * 0.5,
          }))
          .sort((a, b) => b.score - a.score);
        // Take top 3 with score>0, else fall back to most-recent 3 to ensure
        // we always emit links (avoids the orphan-sitemap regression).
        const top = scored.filter((s) => s.score > 0).slice(0, 3);
        const fillers = scored.filter((s) => s.score === 0).slice(0, 3 - top.length);
        setRelated([...top, ...fillers].map((s) => s.post));

        // Rank products by name-overlap with the article. We bias here
        // toward in-stock products only, a dead-link from the blog to an
        // out-of-stock SKU would burn the click + bounce-rate signal.
        const scoredProducts = allProducts
          .filter((pr) => pr.in_stock !== false)
          .map((pr) => ({
            prod: pr,
            score: _scoreOverlap(currentText, pickLocalized(pr.name)) * 2 + _scoreOverlap(currentText, pickLocalized(pr.description)),
          }))
          .sort((a, b) => b.score - a.score);
        const topP = scoredProducts.filter((s) => s.score > 0).slice(0, 3);
        const fillerP = scoredProducts.filter((s) => s.score === 0).slice(0, 3 - topP.length);
        setProducts([...topP, ...fillerP].map((s) => s.prod));
      } catch (e) {
        // Silent failure, the rest of the article still renders cleanly.
        // We never want a related-content lookup to break the page.
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPost.id, currentLanguage]);

  if (related.length === 0 && products.length === 0) return null;

  return (
    <div className="mt-10 space-y-10" data-testid="related-posts-section">
      {related.length > 0 && (
        <div>
          <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-capsule-teal" />
            {_label(currentLanguage, 'relatedPosts')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {related.map((p) => (
              <Link
                key={p.id}
                to={`/blog/${p.slug}`}
                className="block p-5 bg-white rounded-2xl border border-gray-200 hover:border-capsule-teal hover:shadow-md transition-all"
                data-testid={`related-post-${p.slug}`}
              >
                <h3 className="font-semibold text-gray-900 text-sm leading-snug mb-2 line-clamp-3">
                  {pickLocalized(p.title)}
                </h3>
                <span className="text-xs text-capsule-teal font-medium">
                  {_label(currentLanguage, 'readArticle')} →
                </span>
              </Link>
            ))}
          </div>
        </div>
      )}

      {products.length > 0 && (
        <div>
          <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <FlaskConical className="w-5 h-5 text-capsule-coral" />
            {_label(currentLanguage, 'relatedProducts')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {products.map((pr) => (
              <Link
                key={pr.id}
                to={`/product/${pr.id}`}
                className="block p-5 bg-white rounded-2xl border border-gray-200 hover:border-capsule-coral hover:shadow-md transition-all"
                data-testid={`related-product-${pr.id}`}
              >
                <h3 className="font-semibold text-gray-900 text-sm leading-snug mb-2 line-clamp-2">
                  {pickLocalized(pr.name)}
                </h3>
                {pr.price && (
                  <p className="text-xs text-gray-600 mb-2">
                    €{Number(pr.price).toFixed(2)}
                  </p>
                )}
                <span className="text-xs text-capsule-coral font-medium">
                  {_label(currentLanguage, 'viewProduct')} →
                </span>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default RelatedPosts;
